import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import type {
  DashboardDateFilterTargetRow,
  DashboardHeaderWidget,
  HeaderCategoryListHeight,
  HeaderCategoryOperator,
  HeaderCategoryPresentation,
  HeaderCategorySelectionMode,
} from './types';
import {
  HEADER_CATEGORY_SCHEMA_OPTIONS,
  HEADER_NUMBER_METRIC_OPTIONS,
} from './headerWidgetSchema';
import { useWebMapContext } from '../webMap/WebMapContext';

const HeaderWidgetSettingsDialog: React.FC<{
  open: boolean;
  widget: DashboardHeaderWidget | null;
  onClose: () => void;
  onSave: (w: DashboardHeaderWidget) => void;
  /** Dashboard tiles shown under Date selector → Actions (filter targets). */
  dateFilterTargets?: DashboardDateFilterTargetRow[];
}> = ({ open, widget, onClose, onSave, dateFilterTargets }) => {
  const webMapCtx = useWebMapContext();
  const [label, setLabel] = useState('');
  const [dateTab, setDateTab] = useState(0);
  const [hintText, setHintText] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [timeExcludedLayoutIds, setTimeExcludedLayoutIds] = useState<string[]>([]);
  const [schemaField, setSchemaField] = useState<string>('type');
  const [categoryLayerId, setCategoryLayerId] = useState<string>('');
  const [placeholderText, setPlaceholderText] = useState('');
  const [presentation, setPresentation] = useState<HeaderCategoryPresentation>('dropdown');
  const [selectionMode, setSelectionMode] = useState<HeaderCategorySelectionMode>('single');
  const [showSearch, setShowSearch] = useState(false);
  const [showResetSelectAll, setShowResetSelectAll] = useState(false);
  const [listHeight, setListHeight] = useState<HeaderCategoryListHeight>('compact');
  const [categoryOperator, setCategoryOperator] = useState<HeaderCategoryOperator>('include');
  const [numMode, setNumMode] = useState<'static' | 'dynamic'>('dynamic');
  const [staticValue, setStaticValue] = useState('');
  const [metric, setMetric] = useState<string>('feature_count');

  useEffect(() => {
    if (!open || !widget) return;
    setLabel(widget.label ?? '');
    setDateTab(0);
    setTargetSearch('');
    if (widget.type === 'header_date') {
      setHintText(widget.hintText ?? '');
      setTimeExcludedLayoutIds([...(widget.timeFilterExcludedWidgetIds ?? [])]);
    }
    if (widget.type === 'header_category') {
      setCategoryLayerId(widget.layerId ?? '');
      setSchemaField(widget.schemaField);
      setPlaceholderText(widget.placeholderText ?? '');
      setPresentation(widget.presentation ?? 'dropdown');
      setSelectionMode(widget.selectionMode ?? 'single');
      setShowSearch(widget.showSearch ?? false);
      setShowResetSelectAll(widget.showResetSelectAll ?? false);
      setListHeight(widget.listHeight ?? 'compact');
      setCategoryOperator(widget.categoryOperator ?? 'include');
    }
    if (widget.type === 'header_number') {
      setNumMode(widget.mode);
      setStaticValue(widget.staticValue ?? '');
      setMetric(widget.metric ?? 'devices_filtered');
    }
  }, [open, widget]);

  const sortedFilterTargets = useMemo(() => {
    const list = dateFilterTargets ?? [];
    return [...list].sort(
      (a, b) => Number(b.perWidgetTimeSupported) - Number(a.perWidgetTimeSupported)
    );
  }, [dateFilterTargets]);

  const visibleFilterTargets = useMemo(() => {
    const q = targetSearch.trim().toLowerCase();
    if (!q) return sortedFilterTargets;
    return sortedFilterTargets.filter(
      (r) =>
        r.label.toLowerCase().includes(q) ||
        r.layoutId.toLowerCase().includes(q) ||
        r.widgetType.toLowerCase().includes(q)
    );
  }, [sortedFilterTargets, targetSearch]);

  const activeTargetCount = useMemo(() => {
    return sortedFilterTargets.reduce((acc, r) => {
      if (r.perWidgetTimeSupported) {
        return acc + (timeExcludedLayoutIds.includes(r.layoutId) ? 0 : 1);
      }
      return acc + 1;
    }, 0);
  }, [sortedFilterTargets, timeExcludedLayoutIds]);

  const categoryLayers = webMapCtx?.layers ?? [];
  const categoryFieldOptions = useMemo(() => {
    if (!webMapCtx) return HEADER_CATEGORY_SCHEMA_OPTIONS.map((option) => option.value);
    const schemas = categoryLayerId
      ? [webMapCtx.layerSchemas.get(categoryLayerId) ?? []]
      : Array.from(webMapCtx.layerSchemas.values());
    const fields = new Set<string>();
    schemas.flat().forEach((column) => {
      if (column.name && !['geom', 'geometry', 'the_geom'].includes(column.name.toLowerCase())) {
        fields.add(column.name);
      }
    });
    if (fields.size === 0) {
      HEADER_CATEGORY_SCHEMA_OPTIONS.forEach((option) => fields.add(option.value));
    }
    return Array.from(fields).sort((a, b) => a.localeCompare(b));
  }, [webMapCtx, categoryLayerId]);

  if (!widget) return null;

  const handleSave = () => {
    const id = widget.id;
    const labelTrim = label.trim() || undefined;
    if (widget.type === 'header_date') {
      const sanitizedExcluded = timeExcludedLayoutIds.filter((layoutId) =>
        dateFilterTargets?.some((r) => r.layoutId === layoutId && r.perWidgetTimeSupported)
      );
      onSave({
        id,
        type: 'header_date',
        label: labelTrim,
        hintText: hintText.trim() || undefined,
        timeFilterExcludedWidgetIds: sanitizedExcluded.length ? sanitizedExcluded : undefined,
      });
    } else if (widget.type === 'header_category') {
      onSave({
        id,
        type: 'header_category',
        label: labelTrim,
        layerId: categoryLayerId || undefined,
        schemaField,
        placeholderText: placeholderText.trim() || undefined,
        presentation,
        selectionMode,
        showSearch,
        showResetSelectAll,
        listHeight,
        categoryOperator,
      });
    } else {
      onSave({
        id,
        type: 'header_number',
        label: labelTrim,
        mode: numMode,
        staticValue: numMode === 'static' ? staticValue.trim() || undefined : undefined,
        metric: numMode === 'dynamic' ? metric : undefined,
      });
    }
    onClose();
  };

  const isDate = widget.type === 'header_date';

  return (
    <Dialog open={open} onClose={onClose} maxWidth={isDate ? 'md' : 'sm'} fullWidth>
      <DialogTitle>
        Header widget —{' '}
        {widget.type === 'header_date'
          ? 'Date selector'
          : widget.type === 'header_category'
            ? 'Category selector'
            : 'Number'}
      </DialogTitle>
      <DialogContent>
        {isDate ? (
          <>
            <Tabs value={dateTab} onChange={(_, v) => setDateTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Selector" />
              <Tab label="Actions" />
            </Tabs>
            <Box sx={{ pt: 2 }}>
              {dateTab === 0 ? (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Sets the time window for the dashboard. Use the Actions tab to choose which
                    tiles follow this window.
                  </Typography>
                  <TextField
                    label="Label (on the control)"
                    fullWidth
                    size="small"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                  />
                  <TextField
                    label="Hint (optional, above the control)"
                    fullWidth
                    size="small"
                    value={hintText}
                    onChange={(e) => setHintText(e.target.value)}
                    helperText="Short caption, e.g. (Select a date)."
                  />
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    When the time window changes, targets that are on use the new range for their data queries.
                  </Typography>
                  <Typography variant="subtitle2">
                    Filter — {activeTargetCount} active target{activeTargetCount === 1 ? '' : 's'}
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Search targets…"
                    value={targetSearch}
                    onChange={(e) => setTargetSearch(e.target.value)}
                    fullWidth
                  />
                  <Box
                    sx={{
                      maxHeight: 360,
                      overflow: 'auto',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {visibleFilterTargets.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No dashboard tiles yet. Add widgets to the grid to see filter targets here.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {visibleFilterTargets.map((row) => {
                          const adjustable = row.perWidgetTimeSupported;
                          const excluded = timeExcludedLayoutIds.includes(row.layoutId);
                          const applied = adjustable ? !excluded : true;
                          return (
                            <Stack
                              key={row.layoutId}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ py: 0.5 }}
                            >
                              <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" noWrap title={row.label}>
                                  {row.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {row.layoutId}
                                </Typography>
                              </Stack>
                              <Chip label={row.widgetType} size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                              <Tooltip
                                title={
                                  adjustable
                                    ? 'Off = tile ignores header time (timeline uses All Time).'
                                    : 'This tile always follows the header time window.'
                                }
                              >
                                <FormControlLabel
                                  sx={{ m: 0, flexShrink: 0 }}
                                  control={
                                    <Switch
                                      size="small"
                                      checked={applied}
                                      disabled={!adjustable}
                                      onChange={(_, checked) => {
                                        if (!adjustable) return;
                                        if (checked) {
                                          setTimeExcludedLayoutIds((prev) =>
                                            prev.filter((id) => id !== row.layoutId)
                                          );
                                        } else {
                                          setTimeExcludedLayoutIds((prev) =>
                                            prev.includes(row.layoutId) ? prev : [...prev, row.layoutId]
                                          );
                                        }
                                      }}
                                    />
                                  }
                                  label={adjustable ? 'Time filter' : 'Shared query'}
                                  labelPlacement="start"
                                />
                              </Tooltip>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              )}
            </Box>
          </>
        ) : (
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {widget.type === 'header_category' &&
              'Choose which layer field powers this filter control. Selector options mirror ArcGIS category selector settings.'}
            {widget.type === 'header_number' &&
              'Show a fixed value or a dynamic metric from the bound data source.'}
          </Typography>

          <TextField
            label="Label (shown above control)"
            fullWidth
            size="small"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />

          {widget.type === 'header_category' && (
            <>
              <FormControl fullWidth size="small">
                <InputLabel id="hw-category-layer">Source layer</InputLabel>
                <Select
                  labelId="hw-category-layer"
                  label="Source layer"
                  value={categoryLayerId}
                  onChange={(e) => {
                    const nextLayerId = String(e.target.value);
                    setCategoryLayerId(nextLayerId);
                    const fields = webMapCtx?.layerSchemas.get(nextLayerId) ?? [];
                    const selectable = fields.filter(
                      (column) => !['geom', 'geometry', 'the_geom'].includes(column.name.toLowerCase()),
                    );
                    if (selectable.length > 0 && !selectable.some((column) => column.name === schemaField)) {
                      setSchemaField(selectable[0].name);
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>All layers (legacy behavior)</em>
                  </MenuItem>
                  {categoryLayers.map((layer) => (
                    <MenuItem key={layer.id} value={layer.id}>
                      <Stack spacing={0}>
                        <Typography variant="body2">
                          {layer.sourceName || layer.table || layer.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {layer.schema}.{layer.table}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {categoryLayers.length === 0 && (
                <Typography variant="caption" color="warning.main">
                  Add and load a Web Map layer to select a specific source.
                </Typography>
              )}

              <Autocomplete
                freeSolo
                size="small"
                options={categoryFieldOptions}
                value={schemaField}
                onChange={(_, value) => setSchemaField(value ?? '')}
                onInputChange={(_, value) => setSchemaField(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter field"
                    helperText={
                      categoryLayerId
                        ? 'Values are read from this field on the selected layer.'
                        : 'Legacy mode reads this field across all layers.'
                    }
                  />
                )}
              />

              <TextField
                label="Placeholder text (no selection)"
                fullWidth
                size="small"
                value={placeholderText}
                onChange={(e) => setPlaceholderText(e.target.value)}
                helperText="Shown when nothing is selected (e.g. “All types”)."
              />
              <Typography variant="subtitle2">Selector options</Typography>

              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Presentation mode
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={presentation}
                  onChange={(_, v: HeaderCategoryPresentation | null) => v != null && setPresentation(v)}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="inline">Inline</ToggleButton>
                  <ToggleButton value="dropdown">Dropdown</ToggleButton>
                </ToggleButtonGroup>

                <Typography variant="caption" color="text.secondary">
                  Selection
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={selectionMode}
                  onChange={(_, v: HeaderCategorySelectionMode | null) => v != null && setSelectionMode(v)}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="single">Single</ToggleButton>
                  <ToggleButton value="multiple">Multiple</ToggleButton>
                </ToggleButtonGroup>

                <FormControlLabel
                  control={<Switch checked={showSearch} onChange={(_, c) => setShowSearch(c)} size="small" />}
                  label="Show search"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showResetSelectAll}
                      onChange={(_, c) => setShowResetSelectAll(c)}
                      size="small"
                    />
                  }
                  label="Show reset and select all"
                />

                <Typography variant="caption" color="text.secondary">
                  List height (dropdown / inline list)
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={listHeight}
                  onChange={(_, v: HeaderCategoryListHeight | null) => v != null && setListHeight(v)}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="compact">Compact</ToggleButton>
                  <ToggleButton value="extended">Extended</ToggleButton>
                </ToggleButtonGroup>

                <Typography variant="caption" color="text.secondary">
                  Operator (how selected values filter devices)
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={categoryOperator}
                  onChange={(_, v: HeaderCategoryOperator | null) => v != null && setCategoryOperator(v)}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="include">Include</ToggleButton>
                  <ToggleButton value="exclude">Does not include</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </>
          )}

          {widget.type === 'header_number' && (
            <>
              <Typography variant="subtitle2">Value source</Typography>
              <ToggleButtonGroup
                exclusive
                value={numMode}
                onChange={(_, v: 'static' | 'dynamic' | null) => v != null && setNumMode(v)}
                size="small"
                fullWidth
              >
                <ToggleButton value="dynamic">Dynamic (live data)</ToggleButton>
                <ToggleButton value="static">Static text</ToggleButton>
              </ToggleButtonGroup>
              {numMode === 'static' ? (
                <TextField
                  label="Static value"
                  fullWidth
                  size="small"
                  value={staticValue}
                  onChange={(e) => setStaticValue(e.target.value)}
                />
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel id="hw-metric">Metric</InputLabel>
                  <Select
                    labelId="hw-metric"
                    label="Metric"
                    value={metric}
                    onChange={(e) => setMetric(e.target.value as string)}
                  >
                    {HEADER_NUMBER_METRIC_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={widget.type === 'header_category' && !schemaField.trim()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HeaderWidgetSettingsDialog;
